import text from '../text.json'
import data from '../data.json'
import names from '../names.json'
import {Formik} from 'formik'
import {statuspageAdvancedSchema} from '../../../utils/validation'
import {advancedFormRef} from '../refs'
import Textarea from '../../../components/core/textarea'
import CustomSelect from '../../../components/core/customSelect'
import Tooltip from '../../../components/core/tooltip'
import {HelpIcon} from '../../../icons'
import InputInfo from '../../../components/core/inputInfo'
import Switch from '../../../components/core/switch'
import {useEffect, useRef, useState} from 'react'
import useOnClickOutside from '../../../utils/hooks/useOutsideClick'
import Input from '../../../components/core/input'
import LimitManager from '../../../utils/limitManager'
import AdvancedFormImage from './AdvancedFormImage'
import renderSkeletonLoaders from '../../../utils/loader'
import {useSelector} from 'react-redux'
import classNames from 'classnames'

const AdvancedForm = ({
    id,
    statuspageData,
    isLimitExceeded,
    getFavicon,
    getLogo,
    getSignedUrl,
    removeBranding,
    setRemoveBranding,
    currentLogoSrc,
    setCurrentLogoSrc,
    currentFaviconSrc,
    setCurrentFaviconSrc,
    subscriptionEnabled,
    setSubscriptionEnabled,
    isDarkMode
}) => {
    const [active, setActive] = useState(null)
    const subscriptionInfoRef = useRef()
    const removeBrandingInfoRef = useRef()
    const logoInfoRef = useRef()
    const faviconInfoRef = useRef()
    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }
    const isProcessing = useSelector((state) => state.statuspage.isProcessing)

    const initialFormValues = {
        description: id ? statuspageData?.common?.description : '',
        page_name: id ? statuspageData?.common?.page_name : '',
        website_url: id ? statuspageData?.common?.website_url : '',
        privacy_url: id ? statuspageData?.common?.privacy_url : '',
        support_url: id ? statuspageData?.common?.support_url : '',
        ga_tracking: id ? statuspageData?.common?.ga_tracking : '',
        robots_meta_contents: id
            ? data.robotsMetaContentsOptions.find(
                (item) =>
                    item.value ===
            statuspageData?.common?.robots_meta_contents?.join(', ')
            )
            : ''
    }

    useEffect(() => {
        if (id) {
            LimitManager.getStatusPageSubscriptionEnabled() &&
        setSubscriptionEnabled(statuspageData?.common?.subscription_enabled)
            LimitManager.getStatusPageBrandingRemovedEnabled() &&
        setRemoveBranding(statuspageData?.common?.remove_branding)
            !statuspageData?.common?.logo && setCurrentLogoSrc(null)
            !statuspageData?.common?.favicon && setCurrentFaviconSrc(null)
        }
    }, [JSON.stringify(statuspageData)])

    useOnClickOutside(subscriptionInfoRef, () =>
        setActive(active === names.subscription_enabled && '')
    )
    useOnClickOutside(removeBrandingInfoRef, () =>
        setActive(active === names.remove_branding && '')
    )
    useOnClickOutside(logoInfoRef, () => setActive(active === 'logo' && ''))
    useOnClickOutside(faviconInfoRef, () =>
        setActive(active === 'favicon' && '')
    )

    return (
        <div className="content advanced">
            <Formik
                innerRef={advancedFormRef}
                initialValues={initialFormValues}
                validateOnBlur={false}
                validateOnChange={false}
                enableReinitialize={true}
                validationSchema={statuspageAdvancedSchema}
                onSubmit
            >
                {({values, errors, handleChange, handleSubmit, setFieldValue}) => (
                    <form onSubmit={handleSubmit} className="form-elements">
                        {isProcessing ? renderSkeletonLoaders(1, 'textarea-skeleton') :
                            <Textarea
                                type="text"
                                name={names.description}
                                value={values.description}
                                error={errors.description}
                                placeholder={text.description_placeholder}
                                infoText={text.description_info}
                                onChange={(e) => {
                                    handleChange(e)
                                    setFieldValue(names.description, e.target.value)
                                }}
                                activeInfo={active === names.description}
                                handleInfo={(state) => handleInfo(names.description, state)}
                                isDisabled={isLimitExceeded && !id}
                            />}
                        {isProcessing ? renderSkeletonLoaders(6) :
                            <>
                                <Input
                                    name={names.page_name}
                                    value={values.page_name}
                                    error={errors.page_name}
                                    placeholder={text.page_name_placeholder}
                                    infoText={text.page_name_info}
                                    onChange={handleChange}
                                    activeInfo={active === names.page_name}
                                    handleInfo={(state) => handleInfo(names.page_name, state)}
                                    inputIsDisabled={isLimitExceeded && !id}
                                />
                                {!LimitManager.getStatusPageRobotMetaContentsEnabled() ?
                                    <Tooltip
                                        trigger="mouseenter"
                                        placement="top"
                                        content={text.premium_feature}
                                    >
                                        <CustomSelect
                                            name={names.robots_meta_contents}
                                            value={values.robots_meta_contents}
                                            options={data.robotsMetaContentsOptions}
                                            placeholder={text.robots_meta_contents}
                                            infoText={text.robots_meta_contents_info}
                                            onChange={(name, data) => setFieldValue(name, data)}
                                            activeInfo={active === names.robots_meta_contents}
                                            handleInfo={(state) =>
                                                handleInfo(names.robots_meta_contents, state)
                                            }
                                            isDisabled={(isLimitExceeded && !id) || !LimitManager.getStatusPageRobotMetaContentsEnabled()}
                                        />
                                    </Tooltip> :
                                    <CustomSelect
                                        name={names.robots_meta_contents}
                                        value={values.robots_meta_contents}
                                        options={data.robotsMetaContentsOptions}
                                        placeholder={text.robots_meta_contents}
                                        infoText={text.robots_meta_contents_info}
                                        onChange={(name, data) => setFieldValue(name, data)}
                                        activeInfo={active === names.robots_meta_contents}
                                        handleInfo={(state) =>
                                            handleInfo(names.robots_meta_contents, state)
                                        }
                                        isDisabled={isLimitExceeded && !id}
                                    />}
                                <Input
                                    name={names.website_url}
                                    value={values.website_url}
                                    error={errors.website_url}
                                    placeholder={text.website_url_placeholder}
                                    infoText={text.website_url_info}
                                    onChange={handleChange}
                                    activeInfo={active === names.website_url}
                                    handleInfo={(state) => handleInfo(names.website_url, state)}
                                    inputIsDisabled={isLimitExceeded && !id}
                                />
                                <Input
                                    name={names.privacy_url}
                                    value={values.privacy_url}
                                    error={errors.privacy_url}
                                    placeholder={text.privacy_url_placeholder}
                                    infoText={text.privacy_url_info}
                                    onChange={handleChange}
                                    activeInfo={active === names.privacy_url}
                                    handleInfo={(state) => handleInfo(names.privacy_url, state)}
                                    inputIsDisabled={isLimitExceeded && !id}
                                />
                                <Input
                                    name={names.support_url}
                                    value={values.support_url}
                                    error={errors.support_url}
                                    placeholder={text.support_url_placeholder}
                                    infoText={text.support_url_info}
                                    onChange={handleChange}
                                    activeInfo={active === names.support_url}
                                    handleInfo={(state) => handleInfo(names.support_url, state)}
                                    inputIsDisabled={isLimitExceeded && !id}
                                />
                                {!LimitManager.getStatusPageGaTrackingEnabled() ? (
                                    <Tooltip
                                        trigger="mouseenter"
                                        placement="top"
                                        content={text.premium_feature}
                                    >
                                        <Input
                                            name={names.ga_tracking}
                                            value={values.ga_tracking}
                                            error={errors.ga_tracking}
                                            placeholder={text.ga_tracking_placeholder}
                                            infoText={text.ga_tracking_info}
                                            onChange={handleChange}
                                            activeInfo={active === names.ga_tracking}
                                            handleInfo={(state) => handleInfo(names.ga_tracking, state)}
                                            inputIsDisabled={(isLimitExceeded && !id) || !LimitManager.getStatusPageGaTrackingEnabled()}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Input
                                        name={names.ga_tracking}
                                        value={values.ga_tracking}
                                        error={errors.ga_tracking}
                                        placeholder={text.ga_tracking_placeholder}
                                        infoText={text.ga_tracking_info}
                                        onChange={handleChange}
                                        activeInfo={active === names.ga_tracking}
                                        handleInfo={(state) => handleInfo(names.ga_tracking, state)}
                                        inputIsDisabled={isLimitExceeded && !id}
                                    />
                                )}
                            </>}
                        {isProcessing ? renderSkeletonLoaders(2, 'textarea-skeleton') :
                            <>
                                {!LimitManager.getStatusPageLogoEnabled() ?
                                    <Tooltip
                                        trigger="mouseenter"
                                        placement="top"
                                        content={text.premium_feature}
                                    >
                                        <AdvancedFormImage
                                            names={{...names, image: 'favicon'}}
                                            errors={errors}
                                            currentSrc={currentFaviconSrc}
                                            setCurrentSrc={setCurrentFaviconSrc}
                                            text={text}
                                            active={active}
                                            setActive={setActive}
                                            imageRef={faviconInfoRef}
                                            getSignedUrl={getSignedUrl}
                                            setFieldValue={setFieldValue}
                                            getImage={getFavicon}
                                            isDisabled={(isLimitExceeded && !id) || !LimitManager.getStatusPageLogoEnabled()}
                                        />
                                    </Tooltip> :
                                    <AdvancedFormImage
                                        names={{...names, image: 'favicon'}}
                                        errors={errors}
                                        currentSrc={currentFaviconSrc}
                                        setCurrentSrc={setCurrentFaviconSrc}
                                        text={text}
                                        active={active}
                                        setActive={setActive}
                                        imageRef={faviconInfoRef}
                                        getSignedUrl={getSignedUrl}
                                        setFieldValue={setFieldValue}
                                        getImage={getFavicon}
                                        isDisabled={isLimitExceeded && !id}
                                    />}
                                {!LimitManager.getStatusPageLogoEnabled() ?
                                    <Tooltip
                                        trigger="mouseenter"
                                        placement="top"
                                        content={text.premium_feature}
                                    >
                                        <AdvancedFormImage
                                            names={{...names, image: 'logo'}}
                                            errors={errors}
                                            currentSrc={currentLogoSrc}
                                            setCurrentSrc={setCurrentLogoSrc}
                                            text={text}
                                            active={active}
                                            setActive={setActive}
                                            imageRef={logoInfoRef}
                                            getSignedUrl={getSignedUrl}
                                            setFieldValue={setFieldValue}
                                            getImage={getLogo}
                                            isDisabled={(isLimitExceeded && !id) || !LimitManager.getStatusPageLogoEnabled()}
                                        />
                                    </Tooltip> :
                                    <AdvancedFormImage
                                        names={{...names, image: 'logo'}}
                                        errors={errors}
                                        currentSrc={currentLogoSrc}
                                        setCurrentSrc={setCurrentLogoSrc}
                                        text={text}
                                        active={active}
                                        setActive={setActive}
                                        imageRef={logoInfoRef}
                                        getSignedUrl={getSignedUrl}
                                        setFieldValue={setFieldValue}
                                        getImage={getLogo}
                                        isDisabled={isLimitExceeded && !id}
                                    />}
                            </>}
                        {isProcessing ? renderSkeletonLoaders(2, 'switch-skeleton') :
                            <>
                                <div className={classNames('subscription', {
                                    dark: isDarkMode
                                })}>
                                    {!LimitManager.getStatusPageSubscriptionEnabled() ? (
                                        <Tooltip
                                            trigger="mouseenter"
                                            placement="top"
                                            content={text.premium_feature}
                                        >
                                            <Switch
                                                name={names.subscription_enabled}
                                                checked={subscriptionEnabled}
                                                onChange={() =>
                                                    setSubscriptionEnabled(!subscriptionEnabled)
                                                }
                                                isDisabled={(isLimitExceeded && !id) || !LimitManager.getStatusPageSubscriptionEnabled()}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Switch
                                            name={names.subscription_enabled}
                                            checked={subscriptionEnabled}
                                            onChange={() => setSubscriptionEnabled(!subscriptionEnabled)}
                                            isDisabled={isLimitExceeded && !id}
                                        />
                                    )}
                                    <p>{text.subscription_enabled_placeholder}</p>
                                    <div
                                        ref={subscriptionInfoRef}
                                        className={`help ${
                                            active === names.subscription_enabled ? 'active' : ''
                                        }`}
                                        onClick={() =>
                                            setActive(
                                                active === names.subscription_enabled
                                                    ? ''
                                                    : names.subscription_enabled
                                            )
                                        }
                                    >
                                        <span>
                                            <HelpIcon />
                                        </span>
                                        <InputInfo text={text.subscription_enabled_info} />
                                    </div>
                                </div>
                                <div className={classNames('subscription', {
                                    dark: isDarkMode
                                })}>
                                    {!LimitManager.getStatusPageBrandingRemovedEnabled() ? (
                                        <Tooltip
                                            trigger="mouseenter"
                                            placement="top"
                                            content={text.premium_feature}
                                        >
                                            <Switch
                                                name={names.remove_branding}
                                                checked={removeBranding}
                                                onChange={() => setRemoveBranding(!removeBranding)}
                                                isDisabled={(isLimitExceeded && !id) || !LimitManager.getStatusPageBrandingRemovedEnabled()}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Switch
                                            name={names.remove_branding}
                                            checked={removeBranding}
                                            onChange={() => setRemoveBranding(!removeBranding)}
                                            isDisabled={isLimitExceeded && !id}
                                        />
                                    )}
                                    <p>{text.remove_branding}</p>
                                    <div
                                        ref={removeBrandingInfoRef}
                                        className={`help ${
                                            active === names.remove_branding ? 'active' : ''
                                        }`}
                                        onClick={() =>
                                            setActive(
                                                active === names.remove_branding
                                                    ? ''
                                                    : names.remove_branding
                                            )
                                        }
                                    >
                                        <span>
                                            <HelpIcon />
                                        </span>
                                        <InputInfo text={text.subscription_enabled_info} />
                                    </div>
                                </div>
                            </>}
                    </form>
                )}
            </Formik>
        </div>
    )
}

export default AdvancedForm
